import { Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import { FreshnessLabel } from './FreshnessLabel';
import { METRICS } from './metrics';
import { OperatorPerformanceDetails } from './OperatorPerformanceDetails';
import { useOperatorPerformance } from './useOperatorPerformance';
import {
  DEFAULT_WINDOW_HOURS,
  getFreshness,
  getWindowHours,
  REFRESH_MINUTES,
} from './utils';

const chipClassName =
  'border-onlight-line-2 dark:border-ondark-line-2 bg-onlight-bg-2 dark:bg-ondark-bg-2 text-onlight-text-1 dark:text-ondark-text-1 flex h-7 items-center gap-3 rounded-lg border px-3 text-sm';
const dividerClassName =
  'bg-onlight-line-3 dark:bg-ondark-line-3 h-4 w-px flex-none';

export function OperatorPerformanceChip() {
  const { view, now } = useOperatorPerformance();

  if (view.kind === 'hidden') {
    return null;
  }

  if (view.kind === 'empty') {
    return (
      <span
        className={clsx(
          chipClassName,
          'text-onlight-text-2 dark:text-ondark-text-2'
        )}
      >
        No alarms handled in the last {DEFAULT_WINDOW_HOURS}h
      </span>
    );
  }

  const { data, stale } = view;
  const freshness = getFreshness(data.generatedAt, stale, now);

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            title={`Last updated ${dayjs(data.generatedAt).format(
              'HH:mm'
            )}. Stats refresh every ${REFRESH_MINUTES} min.`}
            className={clsx(
              chipClassName,
              'hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3 focus-visible:ring-primary-500 outline-none focus-visible:ring-2',
              open && 'border-primary-500 dark:border-primary-500'
            )}
          >
            <span className="text-onlight-text-2 dark:text-ondark-text-2">
              Last {getWindowHours(data)}h
            </span>
            <span className={dividerClassName} />
            {METRICS.map((metric) => (
              <span
                key={metric.key}
                className={clsx(
                  'flex items-center gap-1.5 whitespace-nowrap',
                  freshness.delayed && 'opacity-60'
                )}
              >
                <span className={metric.swatchClass} />
                <span className="font-semibold">{data.totals[metric.key]}</span>
                <span className="text-onlight-text-2 dark:text-ondark-text-2 sr-only xl:not-sr-only">
                  {metric.label.toLowerCase()}
                </span>
              </span>
            ))}
            <span className={dividerClassName} />
            <FreshnessLabel freshness={freshness} compact />
            <ChevronDownIcon
              className={clsx(
                'text-onlight-text-2 dark:text-ondark-text-2 h-4 w-4 flex-none transition-transform',
                open && 'rotate-180'
              )}
            />
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Popover.Panel className="border-onlight-line-2 dark:border-ondark-line-2 bg-onlight-bg-1 dark:bg-ondark-bg-1 absolute right-0 z-20 mt-2 rounded-xl border p-4 shadow-lg focus:outline-none">
              <OperatorPerformanceDetails data={data} freshness={freshness} />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default OperatorPerformanceChip;
